import { Fragment, useEffect, useRef, useState } from "react";
import Teleprompter from "./Teleprompter";
import {
  BLOCK_BUFFER_SEC,
  BRACKET_INNER,
  PLACEHOLDER_INNER,
  SCRIPT_TOKEN_REGEX,
  Segment,
  distributeTiming,
  emojify,
  parseScript,
} from "../utils";

interface Props {
  text: string;
  maxSec: number;
}

const FRAME_W = 240;
const FRAME_H = 426;
const SCALE = 0.5;

export default function TeleprompterPreview({ text, maxSec }: Props) {
  const trimmed = text.trim();
  const parsed = parseScript(text);
  const timing = distributeTiming(parsed.segments, maxSec);
  const totalDur = timing.length
    ? timing[timing.length - 1].start + timing[timing.length - 1].dur
    : 0;

  const realSegmentCount = parsed.segments.filter(
    (s) => s.kind !== "buffer",
  ).length;
  const isMultiBlock = parsed.blockCount > 1;

  // Group consecutive non-buffer segments by blockIndex for the list view.
  // We keep flat indices so highlight + jump-to stay aligned with the
  // `timing` array (which includes buffers).
  type Entry = { flatIdx: number; seg: Segment };
  type Group = { blockIdx: number; entries: Entry[] };
  const groups: Group[] = [];
  parsed.segments.forEach((seg, flatIdx) => {
    if (seg.kind === "buffer") return;
    const blockIdx = seg.blockIndex ?? 0;
    let g = groups[groups.length - 1];
    if (!g || g.blockIdx !== blockIdx) {
      g = { blockIdx, entries: [] };
      groups.push(g);
    }
    g.entries.push({ flatIdx, seg });
  });
  const blockDurations = groups.map((g) =>
    g.entries.reduce((s, e) => s + (timing[e.flatIdx]?.dur ?? 0), 0),
  );

  const [elapsed, setElapsed] = useState(0);
  const [playing, setPlaying] = useState(false);
  const rafRef = useRef(0);
  const lastTickRef = useRef(0);

  useEffect(() => {
    if (!playing || totalDur <= 0) return;
    lastTickRef.current = performance.now();
    const loop = (now: number) => {
      const dt = (now - lastTickRef.current) / 1000;
      lastTickRef.current = now;
      setElapsed((e) => {
        const next = e + dt;
        if (next >= totalDur) {
          setPlaying(false);
          return totalDur;
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, totalDur]);

  useEffect(() => {
    if (elapsed > totalDur) setElapsed(totalDur);
  }, [totalDur, elapsed]);

  const activeIdx = (() => {
    for (let i = 0; i < timing.length; i++) {
      if (elapsed >= timing[i].start && elapsed < timing[i].start + timing[i].dur)
        return i;
    }
    return timing.length > 0 ? timing.length - 1 : -1;
  })();
  const activeSeg = activeIdx >= 0 ? parsed.segments[activeIdx] : undefined;
  const inBuffer = activeSeg?.kind === "buffer";

  const togglePlay = () => {
    if (totalDur <= 0) return;
    if (elapsed >= totalDur) setElapsed(0);
    setPlaying((p) => !p);
  };
  const reset = () => {
    setPlaying(false);
    setElapsed(0);
  };
  const jumpTo = (flatIdx: number) => {
    setElapsed(timing[flatIdx]?.start ?? 0);
  };

  return (
    <>
      <div className="flex justify-between text-xs text-concrete">
        <span>
          {realSegmentCount} segment(s)
          {isMultiBlock && ` · ${parsed.blockCount} vidéos`}
          {parsed.intro.length > 0 && ` · ${parsed.intro.length} intro line(s)`}
          {totalDur > 0 && ` · ${totalDur.toFixed(1)}s total`}
        </span>
        <span>{text.length} / 5000</span>
      </div>

      {trimmed && (
        <div className="mt-3 rounded-xl bg-charcoal p-4 border border-concrete">
          <div className="flex flex-col md:flex-row gap-4 items-start">
            {/* Mini 9:16 animated preview */}
            <div className="flex flex-col items-center gap-2 shrink-0 mx-auto md:mx-0">
              <div
                className="rounded-xl bg-black overflow-hidden relative ring-1 ring-ivory/10"
                style={{ width: FRAME_W, height: FRAME_H }}
              >
                <div
                  style={{
                    width: FRAME_W / SCALE,
                    height: FRAME_H / SCALE,
                    transform: `scale(${SCALE})`,
                    transformOrigin: "top left",
                    position: "absolute",
                    top: 0,
                    left: 0,
                  }}
                >
                  <Teleprompter
                    intro={parsed.intro}
                    segments={parsed.segments}
                    timing={timing}
                    elapsedSec={elapsed}
                    phase="recording"
                  />
                </div>
                {inBuffer && (
                  <div className="absolute inset-x-0 bottom-2 flex justify-center pointer-events-none">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-ivory/40">
                      coupe
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={togglePlay}
                  disabled={totalDur <= 0}
                  className="w-10 h-10 rounded-full bg-lemonade text-charcoal font-bold flex items-center justify-center hover:scale-105 transition disabled:opacity-40 disabled:hover:scale-100"
                  title={playing ? "Pause" : "Play"}
                  aria-label={playing ? "Pause" : "Play"}
                >
                  {playing ? "⏸" : "▶"}
                </button>
                <button
                  type="button"
                  onClick={reset}
                  className="w-10 h-10 rounded-full bg-ivory/10 text-ivory border border-ivory/20 hover:bg-ivory/20 transition flex items-center justify-center"
                  title="Reset"
                  aria-label="Reset"
                >
                  ↻
                </button>
              </div>
              <div className="text-[11px] font-mono text-ivory/70 tabular-nums">
                {formatTime(elapsed)} / {formatTime(totalDur)}
              </div>
            </div>

            {/* Segment list — grouped by block, clickable jump-to */}
            <div className="flex-1 space-y-2 min-w-0 w-full">
              <div className="text-[10px] uppercase tracking-widest text-stone/60 font-semibold">
                Preview · click a segment to jump
              </div>

              {parsed.intro.length > 0 && (
                <div className="space-y-1 pb-2 border-b border-concrete/40">
                  <div className="text-[9px] uppercase tracking-widest text-stone/40 font-semibold">
                    Avant l&apos;enregistrement
                  </div>
                  {parsed.intro.map((line, i) => (
                    <div
                      key={`intro-${i}`}
                      className="text-sm text-ivory/80 leading-relaxed"
                    >
                      {renderPreviewLine(line)}
                    </div>
                  ))}
                </div>
              )}

              {groups.length > 0 && (
                <div className="space-y-1">
                  {parsed.intro.length > 0 && !isMultiBlock && (
                    <div className="text-[9px] uppercase tracking-widest text-stone/40 font-semibold">
                      Pendant l&apos;enregistrement
                    </div>
                  )}
                  {groups.map((g, gi) => (
                    <Fragment key={`b-${g.blockIdx}`}>
                      {isMultiBlock && (
                        <div className="flex items-center gap-2 pt-2 first:pt-0">
                          <span className="text-[10px] uppercase tracking-widest text-lemonade/80 font-semibold">
                            Vidéo {gi + 1} / {groups.length}
                          </span>
                          <span className="text-[10px] font-mono text-stone/50 tabular-nums">
                            {blockDurations[gi].toFixed(1)}s
                          </span>
                          <div className="flex-1 h-px bg-concrete/40" />
                        </div>
                      )}
                      {g.entries.map(({ flatIdx, seg }) => {
                        const active = flatIdx === activeIdx;
                        const explicit = seg.duration !== undefined;
                        return (
                          <button
                            type="button"
                            key={`seg-${flatIdx}`}
                            onClick={() => jumpTo(flatIdx)}
                            className={`w-full flex items-baseline gap-3 text-left rounded px-2 py-1 transition ${
                              active
                                ? "bg-ivory/10 ring-1 ring-lemonade/40"
                                : "hover:bg-ivory/5"
                            }`}
                          >
                            <span
                              className={`font-mono text-xs tabular-nums w-12 shrink-0 text-right ${
                                explicit ? "text-ivory/80" : "text-stone/50"
                              }`}
                              title={explicit ? "explicit (N)" : "auto ()"}
                            >
                              {timing[flatIdx]
                                ? `${timing[flatIdx].dur.toFixed(1)}s`
                                : ""}
                            </span>
                            <span
                              className={`font-semibold flex-1 whitespace-pre-line text-sm leading-relaxed ${
                                active ? "text-ivory" : "text-ivory/90"
                              }`}
                            >
                              {renderPreviewLine(seg.raw)}
                            </span>
                          </button>
                        );
                      })}
                      {gi < groups.length - 1 && (
                        <div className="flex items-center gap-2 py-1.5 opacity-60">
                          <div className="flex-1 border-t border-dashed border-concrete/40" />
                          <span className="text-[9px] font-mono uppercase tracking-wider text-stone/50">
                            coupe {BLOCK_BUFFER_SEC.toFixed(1)}s
                          </span>
                          <div className="flex-1 border-t border-dashed border-concrete/40" />
                        </div>
                      )}
                    </Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function formatTime(s: number): string {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

function renderPreviewLine(rawLine: string) {
  const line = emojify(rawLine);
  const parts = line.split(SCRIPT_TOKEN_REGEX).filter(Boolean);
  return parts.map((p, j) => {
    const ph = p.match(PLACEHOLDER_INNER);
    if (ph) {
      return (
        <span key={j} className="text-lemonade font-bold">
          {ph[1]}
        </span>
      );
    }
    const br = p.match(BRACKET_INNER);
    if (br) {
      return (
        <span key={j} className="text-orange-400 font-bold">
          {br[1]}
        </span>
      );
    }
    return <span key={j}>{p}</span>;
  });
}
